import { useAtom, useAtomValue } from 'jotai';
import { useState } from 'react';
import { NavLink, useLocation } from 'react-router';
import { cx, SearchField, SplitDivider, Wordmark } from '../../ui';
import { ApiStatus } from '../ApiStatus';
import { useSession } from '../auth/useSession';
import { AccountMenu } from './AccountMenu';
import { SearchPopover } from './CommandMenu';
import { FulfillmentNav } from './FulfillmentNav';
import { NavIcon } from './NavIcon';
import { useNavCounts } from './navCounts';
import { navItem, navSectionGap } from './navItem';
import { ObjectNavList } from './ObjectNavList';
import { SidebarToggle } from './PageHeader';
import { PersonalSettingsRow, SettingsNavigation, SignOutRow } from './SettingsNavigation';
import { sidebarCollapsedAtom, sidebarWidthAtom } from './shellState';
import { roleFoldDefault } from './sidebarPrefs';
import { TaskBadge } from './TaskBadge';

/** S1-1, S1-2 and S1-6: one inset material, original symbols, two navigation lists. */
export function Sidebar({
  preview = false,
  mode,
  collapsed: previewCollapsed,
}: {
  readonly preview?: boolean;
  readonly mode?: 'app' | 'settings';
  readonly collapsed?: boolean;
}) {
  const location = useLocation();
  const collapsed = useAtomValue(sidebarCollapsedAtom);
  const [storedWidth, setStoredWidth] = useAtom(sidebarWidthAtom);
  const [query, setQuery] = useState('');
  const settings =
    mode === undefined ? location.pathname.startsWith('/settings') : mode === 'settings';
  if (previewCollapsed ?? (collapsed && !preview)) return null;

  /**
   * THE FOOT OF THE COLUMN (redesign register R-S1, R-S3; sidebar variant B). The Settings row,
   * the Appearance menu row, the Sign out row and the name-and-address block left the app
   * sidebar for ONE account button whose menu holds them (`AccountMenu`). Above it, the server
   * line, which is there only while the server does not answer.
   *
   * The block still stands BELOW the scrolling list as a flex sibling, not over it: the rows
   * reach the end of the list and the block begins (wave 5, package A3, decision 9; hig:
   * https://developer.apple.com/design/human-interface-guidelines/scroll-views keeps the scroll
   * edge effect for a scroll view "behind floating interface elements").
   *
   * In the settings sidebar the person's rows and Sign out are rows of the list itself (A5-5),
   * so its foot carries only the server line.
   */
  const bottom = (
    <div className="shell-sidebar-bottom">
      <div className="shell-sidebar-footer">
        <ApiStatus variant="inline" />
        {!settings && <AccountMenu />}
      </div>
    </div>
  );

  /**
   * SH-A10: THE COLUMN IS AS WIDE AS THE PERSON DRAGGED IT, and the token is the ideal
   * everyone starts from. The width is read from the token when nobody has dragged, so the
   * splitter can announce a number and the drag has somewhere to start; `--sidebar-width`
   * is then set on the element itself, which is the one place the column's width is stated.
   * A gallery specimen keeps the token, because a specimen is a picture of the default.
   */
  const width = storedWidth ?? readSidebarWidth();

  return (
    <aside
      className={cx('shell-sidebar', preview && 'shell-sidebar-preview')}
      aria-label={settings ? 'Settings sidebar' : 'App sidebar'}
      style={preview ? undefined : { ['--sidebar-width' as string]: `${width}px` }}
    >
      {!preview && (
        <SplitDivider
          label="Resize the sidebar"
          width={width}
          min={readNumber('--sidebar-width-min', 200)}
          max={readNumber('--sidebar-width-max', 320)}
          onWidth={setStoredWidth}
        />
      )}
      {settings ? (
        <>
          {/*
            THE SETTINGS SIDEBAR CARRIES THE SAME BAND AND THE SAME WORDMARK (wave 5 round 3,
            package A4; the owner at 08:55: "Bei Settings fehlt das OFM OS Logo"). The band
            was already shared - `.shell-sidebar-band`, the kit's 52px Left Pane titlebar
            with the toggle at a trailing inset of 8 - but its leading half was left empty
            here, so walking into settings took the product's name off the window. The kit
            draws ONE window, and the pane's band does not change identity when the list in
            it does.
            kit: `Apple macOS 27 UI Kit.sketch / Windows/Light/Unified Toolbar + Title +
            Sidebar/Active`, Left Pane/Titlebar and Toolbar 256x52 with Sidebar Toggle 36x36
            at (212,8) - the same master both branches instance.
            The mark itself is the product's own artwork and claims nothing from Apple
            (OPEN, branding; `ui/Wordmark`). It links Home from both panes, which is where
            it already led from the app sidebar.
          */}
          <div className="shell-sidebar-band">
            <div className="shell-brand">
              <NavLink to="/" className="hit-expand inline-block rounded-control">
                <Wordmark className="text-md" />
              </NavLink>
            </div>
            <SidebarToggle />
          </div>
          <div className="shell-sidebar-search">
            <SearchField
              context="sidebar"
              label="Search settings"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <div className="shell-sidebar-list">
            <div className="shell-sidebar-scroll">
              <div className="px-(--nav-item-inset)">
                {/*
                  SETTINGS VARIANT B WITH ITS TWO MOVES (register A5-5): the way back to the app
                  stands first under the account block and reads Dashboard, because Home is gone
                  (H1); the person's own row, Sidebar, and Sign out close the list (Appearance
                  became submenus of the account menu, A8-7). The way back keeps the symbol the
                  app sidebar's Dashboard row draws.
                */}
                <SettingsNavigation
                  query={query}
                  activePath={preview ? '/settings/objects' : undefined}
                  leading={
                    <ul>
                      <li>
                        <NavLink to="/" end className={navItem}>
                          <NavIcon icon="dashboard" />
                          <span className="truncate">Dashboard</span>
                        </NavLink>
                      </li>
                    </ul>
                  }
                  trailing={
                    <>
                      <ul>
                        <li>
                          <PersonalSettingsRow />
                        </li>
                      </ul>
                      <ul>
                        <li>
                          <SignOutRow />
                        </li>
                      </ul>
                    </>
                  }
                />
              </div>
            </div>
          </div>
          {bottom}
        </>
      ) : (
        <>
          {/*
            THE PANE'S OWN TITLEBAR BAND (package A3, decision 9 and draft section 9). The kit
            runs the window's 52px Titlebar and Toolbar across BOTH panes and seats the Sidebar
            Toggle in the left one, at x 212 of a 256 pane - so the toggle belongs to the pane
            it opens, at a trailing inset of 256 - (212 + 36) = 8, and the wordmark takes the
            leading half of the same band instead of a padding block of its own.
            kit: `Apple macOS 27 UI Kit.sketch / Windows/Light/Unified Toolbar + Title + Sidebar/Active`,
            Left Pane/Titlebar and Toolbar 256x52 with Sidebar Toggle 36x36 at (212,8).
          */}
          <div className="shell-sidebar-band">
            <div className="shell-brand">
              <NavLink to="/" className="hit-expand inline-block rounded-control">
                <Wordmark className="text-md" />
              </NavLink>
            </div>
            <SidebarToggle />
          </div>
          {/*
            THE GLOBAL SEARCH LIVES HERE NOW, FIELD AND PANEL TOGETHER (wave 4 round 1,
            C2-4). The row was a BUTTON that opened a centred palette; it is the field
            itself, and the results hang under it as a popover the way Mail and Finder
            show theirs. `SearchPopover` owns both halves, so the anchor and the panel
            cannot drift apart.
          */}
          <div className="shell-sidebar-search">
            <SearchPopover />
          </div>
          <div className="shell-sidebar-list">
            <div className="shell-sidebar-scroll">
              <nav className="px-(--nav-item-inset)" aria-label="Objects">
                <AppNavigation />
              </nav>
            </div>
          </div>
          {bottom}
        </>
      )}
    </aside>
  );
}

/**
 * SIDEBAR VARIANT B (redesign register A5-4, quoted: "Dashboard, Onboarding, Clients, then My
 * tasks and Reports as a second short block (no headings); below them today's groups (Client
 * data, Delivery, Sales, Team, System) as headings that each fold, closed by default for Ben and
 * the coaches (per role), a group opens itself when the current page is one of its lists;
 * per-person override kept").
 *
 * GONE: the Home row (H1, the Dashboard is `/`), the "Fulfillment" and "Review" headings, and the
 * "Pending changes" row (R-S1: reached inside My tasks; `/pending` stays a route). The second
 * block stands after the kit's group gap (`navSectionGap`), with no heading over it.
 */
function AppNavigation() {
  const session = useSession();
  const counts = useNavCounts();
  return (
    <>
      <FulfillmentNav counts={counts} />
      <ul className={navSectionGap}>
        <li>
          <NavLink to="/tasks" className={navItem}>
            {/* Its own drawing, list.bullet.clipboard.fill; Onboarding keeps `tasks` (register A7-S2). */}
            <NavIcon icon="clipboard" />
            <span className="flex-1 truncate">My tasks</span>
            <TaskBadge />
          </NavLink>
        </li>
        <li>
          <NavLink to="/reports" className={navItem}>
            <NavIcon icon="reports" />
            <span className="flex-1 truncate">Reports</span>
          </NavLink>
        </li>
      </ul>
      <ObjectNavList foldDefault={roleFoldDefault(session.data?.roles)} />
    </>
  );
}

/**
 * THE THREE WIDTHS COME FROM THE TOKENS, not from a second copy of the numbers here: the
 * ideal, the minimum and the maximum are stated once in `tokens.css` with the kit master
 * each rests on, and a component that re-typed them would be the same defect the toolbar
 * had - two places holding one construction and disagreeing. The fallbacks are for a
 * document that has not applied the stylesheet yet (the first paint of a test render);
 * they are the same numbers, and the drag corrects itself on the next frame.
 */
function readNumber(token: string, fallback: number): number {
  if (typeof window === 'undefined') return fallback;
  const value = Number.parseFloat(
    getComputedStyle(document.documentElement).getPropertyValue(token),
  );
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

function readSidebarWidth(): number {
  return readNumber('--sidebar-width', 256);
}
